import React from 'react';
import { motion } from 'framer-motion';

interface SelectControlProps {
    value: string;
    onChange: (newValue: string) => void;
    label: string;
    id: string;
    options: string[];
}

// Icon mapping for common selection options
const getIconForOption = (option: string): React.ReactNode => {
    // Handle non-string options gracefully
    if (typeof option !== 'string') {
        option = String(option);
    }
    const optionLower = option.toLowerCase();
    
    // Axis/Direction icons
    if (optionLower.includes('direct') || optionLower.includes('axis')) {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <path d="M12 2v20M2 12h20" />
                <path d="m8 8 4-4 4 4M8 16l4 4 4-4" />
            </svg>
        );
    }
    
    // Fixed/Lock icons
    if (optionLower.includes('fixed') || optionLower.includes('lock')) {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <circle cx="12" cy="16" r="1" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
        );
    }
    
    // Orbital/Circular icons
    if (optionLower.includes('orbital') || optionLower.includes('circle') || optionLower.includes('rotate')) {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <circle cx="12" cy="12" r="9" />
                <circle cx="12" cy="12" r="3" />
                <path d="M12 3c0 4.5-2.5 8-5 8s-5-3.5-5-8 2.5-8 5-8 5 3.5 5 8z" />
            </svg>
        );
    }
    
    // Auto/Automatic icons
    if (optionLower.includes('auto')) {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <circle cx="12" cy="12" r="3" />
                <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24" />
            </svg>
        );
    }
    
    // Manual/Hand icons
    if (optionLower.includes('manual') || optionLower.includes('hand')) {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <path d="M18 11V6a2 2 0 0 0-2-2h-1a2 2 0 0 0-2 2v5" />
                <path d="M14 10V4a2 2 0 0 0-2-2h-1a2 2 0 0 0-2 2v2" />
                <path d="M10 10.5V6a2 2 0 0 0-2-2h-1a2 2 0 0 0-2 2v8" />
                <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-4a8 8 0 0 1-8-8V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
        );
    }
    
    // On/Off icons
    if (optionLower === 'on' || optionLower === 'enabled') {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <circle cx="12" cy="12" r="10" />
                <path d="M8 12l2 2 4-4" />
            </svg>
        );
    }
    
    if (optionLower === 'off' || optionLower === 'disabled') {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <circle cx="12" cy="12" r="10" />
                <path d="M15 9l-6 6M9 9l6 6" />
            </svg>
        );
    }
    
    // Quality icons
    if (optionLower === 'low') {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <path d="M7 7h2v10H7z" />
            </svg>
        );
    }
    
    if (optionLower === 'medium' || optionLower === 'mid') {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <path d="M7 7h2v10H7zM11 7h2v10h-2z" />
            </svg>
        );
    }
    
    if (optionLower === 'high' || optionLower === 'ultra') {
        return (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <path d="M7 7h2v10H7zM11 7h2v10h-2zM15 7h2v10h-2z" />
            </svg>
        );
    }
    
    // Default generic icon
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="2" />
        </svg>
    );
};

export const SelectControl = ({ value, onChange, label, id, options }: SelectControlProps) => {
    return (
        <div style={styles.container}>
            {/* Label */}
            <motion.label 
                htmlFor={id} 
                style={styles.label}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
            >
                {label}
            </motion.label>
            
            {/* Button Group */}
            <div style={styles.buttonGroup}>
                {options.map((option, index) => {
                    const isSelected = value === option;

                    return (
                        <motion.div
                            key={option}
                            initial={{ opacity: 0, scale: 0.8, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ delay: index * 0.05, duration: 0.3 }}
                            whileHover={{ 
                                scale: 1.05,
                                y: -2,
                            }}
                            whileTap={{ scale: 0.95 }}
                            style={{
                                ...styles.button,
                                ...(isSelected ? styles.buttonSelected : {}),
                            }}
                            onClick={() => onChange(option)}
                            title={`Select ${option}`}
                        >
                            {/* Cosmic glow effect for selected */}
                            {isSelected && (
                                <motion.div
                                    style={styles.selectedGlow}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                />
                            )}
                            
                            {/* Icon */}
                            <div style={{
                                ...styles.iconContainer,
                                color: isSelected ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)',
                            }}>
                                {getIconForOption(option)}
                            </div>
                            
                            {/* Option Text */}
                            <span style={{
                                ...styles.optionText,
                                color: isSelected ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)',
                            }}>
                                {option}
                            </span>

                            {/* Green selection indicator */}
                            {isSelected && (
                                <motion.div
                                    style={styles.selectedIndicator}
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
                                />
                            )}
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
};

// --- Cosmic Style Definitions ---
const styles: { [key: string]: React.CSSProperties } = {
    container: {
        marginBottom: '16px',
        width: '100%',
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: 'rgba(255, 255, 255, 0.9)',
        marginBottom: '12px',
        letterSpacing: '0.3px',
        textShadow: '0 0 10px rgba(255, 255, 255, 0.2)',
        fontFamily: "'Inter', system-ui, sans-serif",
    },
    buttonGroup: {
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    button: {
        position: 'relative',
        background: `
            linear-gradient(135deg, 
                rgba(255, 255, 255, 0.06) 0%, 
                rgba(255, 255, 255, 0.02) 100%
            )
        `,
        backdropFilter: 'blur(15px) saturate(180%)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'rgba(255, 255, 255, 0.08)',
        borderRadius: '10px',
        padding: '8px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: `
            0 4px 16px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.06)
        `,
        overflow: 'hidden',
        width: '42px',
        height: '42px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center' as const,
    },
    buttonSelected: {
        background: `
            linear-gradient(135deg, 
                rgba(255, 255, 255, 0.12) 0%, 
                rgba(255, 255, 255, 0.06) 100%
            )
        `,
        borderColor: 'rgba(255, 255, 255, 0.15)',
        boxShadow: `
            0 6px 24px rgba(0, 0, 0, 0.12),
            inset 0 1px 0 rgba(255, 255, 255, 0.1)
        `,
        transform: 'translateY(-1px)',
    },
    selectedGlow: {
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        right: '-50%',
        bottom: '-50%',
        background: `
            radial-gradient(circle at center, 
                rgba(255, 255, 255, 0.1) 0%, 
                rgba(255, 255, 255, 0.03) 30%, 
                transparent 70%
            )
        `,
        borderRadius: '50%',
        zIndex: -1,
        pointerEvents: 'none',
    },
    iconContainer: {
        marginBottom: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
        filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.2))',
    },
    optionText: {
        fontSize: '9px',
        fontWeight: '500',
        letterSpacing: '0.1px',
        textAlign: 'center' as const,
        transition: 'all 0.3s ease',
        fontFamily: "'Inter', system-ui, sans-serif",
        textTransform: 'capitalize' as const,
        lineHeight: '1.1',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
    },
    selectedIndicator: {
        position: 'absolute',
        top: '4px',
        right: '4px',
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: 'rgba(50, 205, 50, 0.9)',
        boxShadow: `
            0 0 12px rgba(50, 205, 50, 0.8),
            0 0 24px rgba(50, 205, 50, 0.4),
            inset 0 1px 0 rgba(255, 255, 255, 0.3)
        `,
        border: '1px solid rgba(50, 205, 50, 0.6)',
        zIndex: 2,
    }
}; 